// src/pages/Orders.js
import Lottie from "lottie-react";
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  PackageOpen,
  ShoppingCart,
  Truck,
  XCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import cartAnimation from "../assets/cartAnimation.json";
import TrackingDetails from "../components/TrackingDetails";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import axios from "../utils/axiosHandler";

const handleDownloadImage = async (imageData, fileName) => {
  try {
    // Axios request for blob
    const response = await axios.get(imageData, { responseType: 'blob' });
    const blob = response.data;

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading image:", error);
    throw new Error("Failed to download image. Please try again.");
  }
};

const DirectImageDownloadButton = ({ imageUrl, fileName, label }) => {
  const handleDirectDownload = async () => {
    try {
      // Handle both old and new URL formats
      const fixedUrl = imageUrl.startsWith("/upload/")
        ? `/api${imageUrl}`
        : imageUrl;

      // Axios request for blob
      const response = await axios.get(fixedUrl, { responseType: 'blob' });
      const blob = response.data;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download the image. Please try again.");
    }
  };

  return (
    <button
      onClick={handleDirectDownload}
      className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded transition-colors"
    >
      {label}
    </button>
  );
};

const downloadOriginalImage = async (orderId, fieldId) => {
  try {
    const response = await axios.get(
      `/api/orders/${orderId}/customization/${fieldId}`,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      }
    );
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fieldId}_original.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading original image:", error);
    alert("Failed to download original image. Please try again.");
  }
};

const ImageDownloadButton = ({ imageData, fileName, label }) => (
  <button
    onClick={() => handleDownloadImage(imageData, fileName)}
    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors"
  >
    {label}
  </button>
);

const CustomizationDetails = ({ customization, orderId, productIndex }) => {
  if (!customization) return null;

  // Helper function to get the correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If it's already a full URL, return as is
    if (imagePath.startsWith("http")) return imagePath;

    // If it starts with /api, return as is
    if (imagePath.startsWith("/api")) return imagePath;

    // If it starts with /upload, prepend /api
    if (imagePath.startsWith("/upload")) return `/api${imagePath}`;

    // If it's a base64 string, return as is
    if (imagePath.startsWith("data:")) return imagePath;

    // Default case
    return imagePath;
  };

  return (
    <div className="mt-4 space-y-3">
      {/* Preview Image with Download */}
      {customization.preview && (
        <div className="flex items-center space-x-2">
          <img
            src={getImageUrl(customization.preview)}
            alt="Preview"
            className="w-20 h-20 object-contain border rounded bg-white"
          />
          <ImageDownloadButton
            imageData={getImageUrl(customization.preview)}
            fileName={`order-${orderId}-product-${productIndex}-preview.png`}
            label="Download Preview"
          />
        </div>
      )}

      {/* Template Info */}
      {customization.template && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Template:</span>{" "}
          {customization.template.name}
        </div>
      )}

      {/* Custom Fields */}
      {customization.customFields?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Custom Elements:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customization.customFields.map((field, idx) => (
              <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium">{field.type}:</span>
                  {field.type === "text" ? (
                    <p className="mt-1 text-gray-600">{field.content}</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {field.imageUrl ? (
                        <div>
                          <img
                            src={
                              field.imageUrl.startsWith("/upload/")
                                ? `/api${field.imageUrl}`
                                : field.imageUrl
                            }
                            alt={`Custom ${field.type}`}
                            className="w-20 h-20 object-contain border rounded bg-white"
                          />
                          <DirectImageDownloadButton
                            imageUrl={field.imageUrl}
                            fileName={`order-${orderId}-product-${productIndex}-custom-${idx}.png`}
                            label="⬇️ Download Image"
                          />
                        </div>
                      ) : (
                        <div>
                          <img
                            src={field.content}
                            alt={`Custom ${field.type}`}
                            className="w-20 h-20 object-contain border rounded bg-white"
                          />
                          <ImageDownloadButton
                            imageData={field.content}
                            fileName={`order-${orderId}-product-${productIndex}-custom-${idx}.png`}
                            label="Download Image"
                          />
                          <button
                            onClick={() =>
                              downloadOriginalImage(orderId, field.fieldId)
                            }
                            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded transition-colors"
                          >
                            🔽 Download Original Image
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {field.properties && (
                    <div className="mt-1 text-xs text-gray-500">
                      {field.properties.fontFamily && (
                        <p>Font: {field.properties.fontFamily}</p>
                      )}
                      {field.properties.fontSize && (
                        <p>Size: {field.properties.fontSize}px</p>
                      )}
                      {field.properties.fill && (
                        <p>Color: {field.properties.fill}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Required Fields */}
      {customization.requiredFields?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Required Fields:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customization.requiredFields.map((field, idx) => (
              <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium">{field.type}:</span>
                  {field.type === "text" ? (
                    <p className="mt-1 text-gray-600">{field.value}</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      <img
                        src={field.value}
                        alt={`Required ${field.type}`}
                        className="w-20 h-20 object-contain border rounded bg-white"
                      />
                      <ImageDownloadButton
                        imageData={field.value}
                        fileName={`order-${orderId}-product-${productIndex}-required-${idx}.png`}
                        label="Download Image"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {customization.description && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Special Instructions:</span>
          <p className="mt-1">{customization.description}</p>
        </div>
      )}
    </div>
  );
};

// Add a new component for tracking progress bar
const OrderProgressTracker = ({ status }) => {
  // Map status to progress percentage
  const getProgressPercentage = () => {
    switch (status) {
      case "pending":
        return 10;
      case "processing":
        return 30;
      case "shipped":
        return 70;
      case "completed":
        return 100;
      case "cancelled":
        return 0;
      default:
        return 0;
    }
  };

  // Get corresponding status color
  const getStatusColor = () => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "processing":
        return "bg-blue-500";
      case "shipped":
        return "bg-indigo-500";
      case "completed":
        return "bg-green-500";
      case "cancelled":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Customize the step indicator content based on status
  const getStepContent = (stepStatus) => {
    // Complete check for completed steps
    if (
      (stepStatus === "pending" &&
        ["pending", "processing", "shipped", "completed"].includes(status)) ||
      (stepStatus === "processing" &&
        ["processing", "shipped", "completed"].includes(status)) ||
      (stepStatus === "shipped" && ["shipped", "completed"].includes(status)) ||
      (stepStatus === "completed" && status === "completed")
    ) {
      return (
        <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full text-white">
          <CheckCircle className="w-6 h-6" />
        </div>
      );
    }

    // Current step (active)
    if (stepStatus === status) {
      return (
        <div
          className={`flex items-center justify-center w-10 h-10 ${getStatusColor()} rounded-full text-white`}
        >
          {stepStatus === "pending" ? (
            <ShoppingCart className="w-6 h-6" />
          ) : stepStatus === "processing" ? (
            <PackageOpen className="w-6 h-6" />
          ) : stepStatus === "shipped" ? (
            <Truck className="w-6 h-6" />
          ) : (
            <CheckCircle className="w-6 h-6" />
          )}
        </div>
      );
    }

    // Future steps
    return (
      <div className="flex items-center justify-center w-10 h-10 bg-gray-200 rounded-full text-gray-400">
        {stepStatus === "pending" ? (
          <ShoppingCart className="w-6 h-6" />
        ) : stepStatus === "processing" ? (
          <PackageOpen className="w-6 h-6" />
        ) : stepStatus === "shipped" ? (
          <Truck className="w-6 h-6" />
        ) : (
          <CheckCircle className="w-6 h-6" />
        )}
      </div>
    );
  };

  // Define the steps
  const steps = [
    { id: "pending", label: "Order Placed" },
    { id: "processing", label: "Processing" },
    { id: "shipped", label: "Shipped" },
    { id: "completed", label: "Delivered" },
  ];

  // Handle cancelled orders separately
  if (status === "cancelled") {
    return (
      <div className="bg-red-50 p-4 rounded-md border border-red-100 text-center">
        <div className="flex items-center justify-center space-x-2 text-red-600">
          <XCircle className="w-6 h-6" />
          <span className="font-medium">Order Cancelled</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Progress bar */}
      <div className="relative pt-1">
        <div className="flex mb-2 items-center justify-between">
          <div>
            <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
              Order Progress
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs font-semibold inline-block text-blue-600">
              {getProgressPercentage()}%
            </span>
          </div>
        </div>
        <div className="flex h-2 mb-4 overflow-hidden text-xs bg-blue-200 rounded">
          <div
            style={{ width: `${getProgressPercentage()}%` }}
            className={`flex flex-col justify-center text-center text-white ${getStatusColor()} transition-all duration-500`}
          ></div>
        </div>
      </div>

      {/* Steps indicators */}
      <div className="flex justify-between">
        {steps.map((step) => (
          <div key={step.id} className="flex flex-col items-center">
            {getStepContent(step.id)}
            <div className="text-xs mt-2 text-center">{step.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Orders = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [activeTrackingTab, setActiveTrackingTab] = useState("summary"); // 'summary', 'details', or 'history'

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    totalPages: 1,
    total: 0,
  });

  useEffect(() => {
    fetchOrders(pagination.page);
  }, [user]);

  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/orders?page=${page}&limit=${pagination.limit}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      setOrders(response.data.orders || []);
      setPagination({
        ...pagination,
        page: response.data.page || 1,
        totalPages: response.data.totalPages || 1,
        total: response.data.total || 0,
      });

      setError(null);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      fetchOrders(newPage);
    }
  };

  const PaginationControls = () => (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6 rounded-lg shadow">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${pagination.page === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
          className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${pagination.page === pagination.totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{" "}
            of <span className="font-medium">{pagination.total}</span> results
          </p>
        </div>
        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${pagination.page === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <span className="sr-only">Previous</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Page numbers */}
            {[...Array(pagination.totalPages).keys()].map((pageNum) => (
              <button
                key={pageNum + 1}
                onClick={() => handlePageChange(pageNum + 1)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pagination.page === pageNum + 1
                  ? "bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  : "bg-white text-gray-900 hover:bg-gray-50"
                  }`}
              >
                {pageNum + 1}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${pagination.page === pagination.totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <span className="sr-only">Next</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  const handleReorder = async (order) => {
    try {
      for (const item of order.products) {
        await addToCart({
          product: item.product,
          quantity: item.quantity,
          customization: item.customization,
        });
      }
      setShowCartNotification(true);
      setError(null);
      setTimeout(() => {
        setShowCartNotification(false);
      }, 3000);
    } catch (error) {
      console.error("Error reordering:", error);
      setError("Failed to reorder items. Please try again.");
    }
  };

  const handleDownloadOrder = async (order) => {
    try {
      setIsDownloading(true);
      const { data } = await axios.get(`/api/orders/${order._id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `order-${order._id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setError(null);
    } catch (error) {
      console.error("Error downloading order:", error);
      setError("Failed to download order. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // if (loading) return (
  //   <div className="flex justify-center items-center h-64">
  //     {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div> */}
  //     <img src='../images/loading.gif'/>
  //   </div>
  // );

  return (
    <div className="relative">
      {/* Download Loading Overlay */}
      {isDownloading && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      )}

      {/* Cart Notification Overlay */}
      {showCartNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
            <Lottie
              animationData={cartAnimation}
              loop={false}
              className="h-24 w-24"
            />
            <p className="text-xl">Item added to cart successfully!</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-4">
        <h2 className="text-2xl font-bold mb-6">
          {user?.isAdmin ? "All Orders" : "My Orders"}
        </h2>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="flex justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 240 160"
                  className="w-40"
                >
                  {/* Background with subtle gradient */}
                  <defs>
                    <linearGradient
                      id="emptyOrderGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
                      <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
                    </linearGradient>
                    <filter
                      id="orderShadow"
                      x="-10%"
                      y="-10%"
                      width="120%"
                      height="120%"
                    >
                      <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="1"
                        floodOpacity="0.05"
                      />
                    </filter>
                  </defs>

                  {/* Order paper */}
                  <rect
                    x="70"
                    y="20"
                    width="100"
                    height="120"
                    rx="4"
                    fill="white"
                    stroke="#e5e7eb"
                    strokeWidth="1.5"
                    filter="url(#orderShadow)"
                  />

                  {/* Order lines */}
                  <line
                    x1="85"
                    y1="40"
                    x2="155"
                    y2="40"
                    stroke="#e5e7eb"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="85"
                    y1="60"
                    x2="155"
                    y2="60"
                    stroke="#e5e7eb"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="85"
                    y1="80"
                    x2="155"
                    y2="80"
                    stroke="#e5e7eb"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="85"
                    y1="100"
                    x2="155"
                    y2="100"
                    stroke="#e5e7eb"
                    strokeWidth="1.5"
                  />

                  {/* Search icon */}
                  <circle
                    cx="150"
                    cy="70"
                    r="30"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    opacity="0.7"
                  />
                  <line
                    x1="170"
                    y1="90"
                    x2="185"
                    y2="105"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    opacity="0.7"
                  />

                  {/* X mark for "not found" */}
                  <line
                    x1="120"
                    y1="50"
                    x2="140"
                    y2="70"
                    stroke="#d1d5db"
                    strokeWidth="2"
                  />
                  <line
                    x1="140"
                    y1="50"
                    x2="120"
                    y2="70"
                    stroke="#d1d5db"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <p className="text-gray-500">No orders found.</p>
            </div>
          ) : (
            orders.map((order) => (
              <div
                key={order._id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                {/* Order Header */}
                <div className="p-6 bg-gray-50 border-b border-t">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-600">
                        Order ID:{" "}
                        <span className="font-medium">{order._id}</span>
                      </p>
                      <p className="text-sm text-gray-600">
                        Date: {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        Status:{" "}
                        <span className="font-medium capitalize">
                          {order.status}
                        </span>
                      </p>
                      <p className="font-bold text-lg mt-1">
                        Total: ${order.totalAmount.toFixed(2)}
                      </p>
                      {/* Coupon Display */}
                      {order.coupon && (
                        <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-semibold text-gray-800">
                                Coupon Applied:
                              </span>
                              <span className="ml-2 text-gray-600">
                                {order.coupon.code}
                              </span>
                            </div>
                            <div className="text-green-600 font-medium">
                              -${order.coupon.discountAmount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleReorder(order)}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                      >
                        Reorder
                      </button>
                      {user?.isAdmin && (
                        <button
                          onClick={() => handleDownloadOrder(order)}
                          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Order Progress Tracker (NEW) */}
                <div className="px-6 py-4 border-b">
                  <OrderProgressTracker status={order.status} />
                </div>

                {/* Shipping Tracking Details (NEW) */}
                {(order.status === "shipped" || order.status === "completed") &&
                  order.shipping && (
                    <div className="px-6 py-4 border-b">
                      {/* Tracking Tabs */}
                      <div className="flex border-b mb-4">
                        <button
                          className={`px-4 py-2 font-medium text-sm ${expandedOrder === `${order._id}-tracking` &&
                            activeTrackingTab === "summary"
                            ? "border-b-2 border-blue-500 text-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                          onClick={() => {
                            setExpandedOrder(`${order._id}-tracking`);
                            setActiveTrackingTab("summary");
                          }}
                        >
                          Shipping Summary
                        </button>
                        <button
                          className={`px-4 py-2 font-medium text-sm ${expandedOrder === `${order._id}-tracking` &&
                            activeTrackingTab === "details"
                            ? "border-b-2 border-blue-500 text-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                          onClick={() => {
                            setExpandedOrder(`${order._id}-tracking`);
                            setActiveTrackingTab("details");
                          }}
                        >
                          Tracking Details
                        </button>
                      </div>

                      {/* Tracking Content based on selected tab */}
                      {expandedOrder === `${order._id}-tracking` && (
                        <div className="pb-4">
                          {activeTrackingTab === "summary" ? (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-gray-600">
                                    Carrier:
                                  </p>
                                  <p className="font-semibold">
                                    {order.shipping.carrier}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">
                                    Tracking Number:
                                  </p>
                                  <div className="flex items-center space-x-2">
                                    <p className="font-semibold">
                                      {order.shipping.trackingNumber}
                                    </p>
                                    <a
                                      href={`https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${order.shipping.trackingNumber}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      Track on carrier website
                                    </a>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">
                                    Shipped Date:
                                  </p>
                                  <p className="font-semibold">
                                    {order.shipping.shippedAt
                                      ? new Date(
                                        order.shipping.shippedAt
                                      ).toLocaleDateString()
                                      : "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">
                                    Estimated Delivery:
                                  </p>
                                  <p className="font-semibold">
                                    {order.shipping.estimatedDelivery
                                      ? new Date(
                                        order.shipping.estimatedDelivery
                                      ).toLocaleDateString()
                                      : "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <TrackingDetails order={order} isAdmin={false} />
                          )}
                        </div>
                      )}
                    </div>
                  )}

                {/* Order Items */}
                <div className="p-6">
                  <div className="space-y-6">
                    {order.products.map((item, index) => (
                      <div
                        key={index}
                        className={index > 0 ? "border-t pt-6" : ""}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="w-32 h-32 flex-shrink-0">
                            <img
                              src={
                                item.customization?.preview?.startsWith(
                                  "/upload/orders/"
                                )
                                  ? `/api${item.customization.preview}`
                                  : item.customization?.preview?.startsWith(
                                    "/upload/"
                                  )
                                    ? `/api${item.customization.preview}`
                                    : item.customization?.preview ||
                                    `/api${item.product.images?.[0]?.path}` ||
                                    item.product.images?.[0]?.data
                              }
                              alt={item.product.name}
                              className="w-full h-full object-contain rounded border bg-gray-50"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-bold text-lg">
                                  {item.product.name}
                                </h3>
                                <p className="text-sm text-gray-600">
                                  Quantity: {item.quantity}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Description: {item.customization.description}
                                </p>
                              </div>
                              <button
                                onClick={() =>
                                  setExpandedOrder(
                                    expandedOrder === `${order._id}-${index}`
                                      ? null
                                      : `${order._id}-${index}`
                                  )
                                }
                                className="text-blue-500 hover:text-blue-600 text-sm"
                              >
                                {expandedOrder === `${order._id}-${index}`
                                  ? "Hide Details"
                                  : "Show Details"}
                              </button>
                            </div>

                            {/* Customization Details */}
                            {expandedOrder === `${order._id}-${index}` && (
                              <CustomizationDetails
                                customization={item.customization}
                                orderId={order._id}
                                productIndex={index}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {orders.length > 0 && <PaginationControls />}
    </div>
  );
};

export default Orders;
